import {
  delPaymentMethod,
  getPaymentMethods as callGetPaymentMethods,
  postPaymentMethod,
} from '~/api/payment';
import { selectUserServiceRegionId } from '~/store/legacy/selector/userSelectors.js';
import { openAlert } from './alertActions';
import * as Sentry from '@sentry/react';
import { SeverityLevel_error, SeverityLevel_info } from '~/constant';

const BASE = 'payment :';

export const paymentActionTypes = {
  choosePaymentMethod: `${BASE} choose method`,
  resetPaymentError: `${BASE} reset error`,

  addPaymentMethodStart: `${BASE} add payment method : persist start`,
  addPaymentMethodSuccess: `${BASE} add payment method : persist SUCCESS`,
  addPaymentMethodFailed: `${BASE} add payment method : persist FAILED`,

  removePaymentMethodStart: `${BASE} remove payment method : persist start`,
  removePaymentMethodSuccess: `${BASE} remove payment method : persist SUCCESS`,
  removePaymentMethodFailed: `${BASE} remove payment method : persist FAILED`,

  getPaymentMethodsStart: `${BASE} methods : fetch start`,
  getPaymentMethodsSuccess: `${BASE} methods : fetch SUCCESS`,
  getPaymentMethodsFailed: `${BASE} methods : fetch FAILED`,
};

export const getPaymentMethods = (isAlipay, userId, loadedOnFinish = true) => async (
  dispatch,
  getState,
  { intl }
) => {
  const state = getState();
  const { formatMessage } = intl(getState());
  Sentry.addBreadcrumb({
    category: 'User Account',
    message: 'Get Payment Method',
    level: 'info',
    data: {
      isAlipay,
    },
  });
  try {
    dispatch({
      type: paymentActionTypes.getPaymentMethodsStart,
    });

    if (!selectUserServiceRegionId(state)) {
      return dispatch({
        type: paymentActionTypes.getPaymentMethodsSuccess,
        methods: [],
        loaded: loadedOnFinish,
      });
    }

    const methods = isAlipay
      ? [
        {
          identifier: 'alipay',
          processor: 'alipay',
        },
      ]
      : await callGetPaymentMethods(userId);
    dispatch({
      type: paymentActionTypes.getPaymentMethodsSuccess,
      methods,
      loaded: loadedOnFinish,
    });
  } catch (e) {
    Sentry.addBreadcrumb({
      category: 'User Account',
      message: 'Get Payment Method - failed to get method',
      level: SeverityLevel_error,
    });
    dispatch(
      openAlert({
        message: formatMessage({ id: 'actions.payment.get.failed' }),
        variant: 'error',
      })
    );
    dispatch({
      type: paymentActionTypes.getPaymentMethodsFailed,
      loaded: loadedOnFinish,
    });
  }
};

export const addPaymentMethod = ({ method, isAlipay, showAlerts = true }) => async (
  dispatch,
  getState
) => {
  Sentry.addBreadcrumb({
    category: 'User Account',
    message: 'Add Payment Method',
    level: SeverityLevel_info,
    data: {
      methodDetails: method?.card || 'malformed method',
      created: method.created,
    },
  });
  try {
    dispatch({
      type: paymentActionTypes.addPaymentMethodStart,
    });
    const paymentMethods = await postPaymentMethod({ methodId: method.id });
    const selectedMethod = paymentMethods.find(
      (paymentMethod) => paymentMethod.identifier === method.id
    );
    const userId = getState()?.user?.user?.id || null;
    await dispatch(getPaymentMethods(isAlipay, userId, false));
    if (showAlerts) {
      dispatch(
        openAlert({
          message: 'Payment has been added',
          variant: 'success',
        })
      );
    }
    dispatch({
      type: paymentActionTypes.addPaymentMethodSuccess,
      selectedMethod,
      loaded: true,
    });
    dispatch(resetPaymentError());
  } catch (e) {
    // Grab payment errors from error response
    let errorMessage = 'Failed to add payment';
    if (typeof e === 'string') {
      errorMessage = e;
    } else if (
      e?.response?.data?.errorList &&
      e?.response?.data?.errorList?.length &&
      e?.response?.data?.errorList[0].title
    ) {
      errorMessage = e?.response?.data?.errorList[0].title;
    }
    Sentry.addBreadcrumb({
      category: 'User Account',
      message: errorMessage,
      level: SeverityLevel_error,
    });
    dispatch(
      openAlert({
        message: errorMessage,
        variant: 'error',
      })
    );
    dispatch({
      type: paymentActionTypes.addPaymentMethodFailed,
      errorMessage,
      loaded: true,
    });
  }
};

export const removePaymentMethod = (method) => async (dispatch) => {
  try {
    dispatch({
      type: paymentActionTypes.removePaymentMethodStart,
    });
    await delPaymentMethod(method);
    dispatch(
      openAlert({
        message: 'Payment method has been removed',
        variant: 'success',
      })
    );
    dispatch({
      type: paymentActionTypes.removePaymentMethodSuccess,
      method,
    });
  } catch (e) {
    dispatch(
      openAlert({
        message: 'Failed to remove payment method',
        variant: 'error',
      })
    );
    dispatch({
      type: paymentActionTypes.removePaymentMethodFailed,
    });
  }
};

export const choosePaymentMethod = (method) => (dispatch) => {
  dispatch({
    type: paymentActionTypes.choosePaymentMethod,
    method,
  });
  dispatch(resetPaymentError());
};

export const resetPaymentError = () => (dispatch) => {
  dispatch({
    type: paymentActionTypes.resetPaymentError,
    errorMessage: '',
  });
};
